import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useExpenseCategories } from "@/hooks/useExpenseCategories";
import { useSuppliers } from "@/hooks/useSuppliers";
import type { ExpenseStatus, ExpenseRow } from "@/hooks/useExpenses";
import {
  SupplierQuickCreateDialog,
  type SupplierQuickCreateValues,
} from "@/components/suppliers/SupplierQuickCreateDialog";

type ExpenseFormMode = "create" | "edit";

export interface ExpenseFormValues {
  title: string;
  description?: string;
  amount: number;
  expense_date: string; // YYYY-MM-DD
  due_date?: string;
  category_id: string;
  supplier_id: string;
  status: ExpenseStatus;
  paid_at?: string; // ISO
}

interface ExpenseFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: ExpenseFormMode;
  expense?: ExpenseRow | null;
  onSubmit: (values: ExpenseFormValues) => Promise<void>;
}

function todayDateOnly() {
  return new Date().toISOString().slice(0, 10);
}

function toLocalDatetimeInputValue(date: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function ExpenseFormDialog({
  open,
  onOpenChange,
  mode,
  expense,
  onSubmit,
}: ExpenseFormDialogProps) {
  const { categories } = useExpenseCategories();
  const { suppliers, createSupplier } = useSuppliers();

  const defaultExpenseDate = useMemo(() => todayDateOnly(), []);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(defaultExpenseDate);
  const [dueDate, setDueDate] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [supplierId, setSupplierId] = useState<string>("");
  const [status, setStatus] = useState<ExpenseStatus>("open");
  const [paidAtLocal, setPaidAtLocal] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supplierQuickCreateOpen, setSupplierQuickCreateOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && expense) {
      setTitle(expense.title);
      setDescription(expense.description || "");
      setAmount(String(Number(expense.amount || 0)));
      setExpenseDate(expense.expense_date || todayDateOnly());
      setDueDate(expense.due_date || "");
      setCategoryId(expense.category_id);
      setSupplierId(expense.supplier_id || "");
      setStatus(expense.status);
      setPaidAtLocal(expense.paid_at ? toLocalDatetimeInputValue(new Date(expense.paid_at)) : "");
      return;
    }

    // create
    setTitle("");
    setDescription("");
    setAmount("");
    setExpenseDate(todayDateOnly());
    setDueDate("");
    setCategoryId("");
    setSupplierId("");
    setStatus("open");
    setPaidAtLocal(toLocalDatetimeInputValue(new Date()));
  }, [open, mode, expense]);

  const parsedAmount = Number(amount);
  const needsPaidAt = status === "paid";

  const canSubmit =
    title.trim().length > 0 &&
    !!categoryId &&
    !!supplierId &&
    !Number.isNaN(parsedAmount) &&
    parsedAmount > 0 &&
    !!expenseDate &&
    (!needsPaidAt || !!paidAtLocal);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        amount: parsedAmount,
        expense_date: expenseDate,
        due_date: dueDate || undefined,
        category_id: categoryId,
        supplier_id: supplierId,
        status,
        paid_at: needsPaidAt ? new Date(paidAtLocal).toISOString() : undefined,
      });
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Editar despesa" : "Nova despesa"}</DialogTitle>
          <DialogDescription>
            {mode === "edit"
              ? "Atualize os dados da despesa manual."
              : "Cadastre uma despesa manual (conta a pagar)."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="expense_title">Título *</Label>
            <Input
              id="expense_title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Aluguel, fornecedor, manutenção..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="expense_amount">Valor *</Label>
            <Input
              id="expense_amount"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
            />
          </div>

          <div className="space-y-2">
            <Label>Status *</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as ExpenseStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Aberta</SelectItem>
                <SelectItem value="paid">Paga</SelectItem>
                <SelectItem value="cancelled">Cancelada</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="expense_date">Data da despesa *</Label>
            <Input
              id="expense_date"
              type="date"
              value={expenseDate}
              onChange={(e) => setExpenseDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="due_date">Vencimento</Label>
            <Input
              id="due_date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Categoria *</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Fornecedor/Credor *</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {suppliers.length === 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Cadastre ao menos um fornecedor/credor para lançar despesas.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSupplierQuickCreateOpen(true)}
                >
                  Cadastrar fornecedor/credor
                </Button>
              </div>
            )}
          </div>

          {needsPaidAt && (
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="paid_at">Data/Hora do pagamento *</Label>
              <Input
                id="paid_at"
                type="datetime-local"
                value={paidAtLocal}
                onChange={(e) => setPaidAtLocal(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="expense_desc">Descrição</Label>
            <Textarea
              id="expense_desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Observações, detalhes, nota fiscal, etc."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>

        <SupplierQuickCreateDialog
          open={supplierQuickCreateOpen}
          onOpenChange={setSupplierQuickCreateOpen}
          onCreate={async (values: SupplierQuickCreateValues) => {
            const id = await createSupplier(values);
            if (id) setSupplierId(id);
            return id;
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
